'use client';

import React, { useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { format, addDays, startOfToday, getDay } from 'date-fns';
import {
  Syringe, Calendar, User, ClipboardList, PenLine, CheckCircle2, Check,
  ArrowLeft, ArrowRight, Loader2, AlertTriangle, Clock, ShieldCheck, MapPin, FileText,
  CreditCard, Camera, Wallet, Ban,
} from 'lucide-react';
import { useTheme } from '@/providers/ThemeProvider';
import { useAuth } from '@/providers/AuthProvider';
import { useVaccines, useConsentTemplate, useVaccineSlots, useSubmitVaccineConsent } from '@/hooks/useVaccines';
import { VaccineStepper, WizardStep } from '@/components/vaccine/VaccineStepper';
import { SignaturePad } from '@/components/vaccine/SignaturePad';
import { PhotoUpload, UploadedPhoto } from '@/components/vaccine/PhotoUpload';
import {
  ConsentField, groupFields, groupDetailSections, deriveContact, missingRequired,
  isFullWidthField, isVaccinesTodayField, isYesNo, findSignatureField, findPhotoField,
  INSURANCE_CHOICES, INSURANCE_CHOICE_KEY, INSURANCE_PHOTOS_KEY, type InsuranceChoice,
} from '@/components/vaccine/consentFields';
import { DayAvailabilityDayEnum } from '@/lib/Apis/models';
import { InsuranceDisclaimer } from '@/components/vaccine/InsuranceDisclaimer';

const WEEKDAY_BY_INDEX: DayAvailabilityDayEnum[] = [
  DayAvailabilityDayEnum.Sunday, DayAvailabilityDayEnum.Monday, DayAvailabilityDayEnum.Tuesday,
  DayAvailabilityDayEnum.Wednesday, DayAvailabilityDayEnum.Thursday, DayAvailabilityDayEnum.Friday,
  DayAvailabilityDayEnum.Saturday,
];

const DAYS_AHEAD = 30;

const INSURANCE_ICON: Record<InsuranceChoice, React.ElementType> = {
  details: CreditCard,
  photo: Camera,
  bring: Wallet,
  none: Ban,
};

const SECTION_ICON: Record<string, React.ReactNode> = {
  personal: <User className="h-4 w-4" />,
  address: <MapPin className="h-4 w-4" />,
  additional: <FileText className="h-4 w-4" />,
};

interface BookVaccineScreenProps {
  /** Called when the user leaves via the back arrow on step 1 (e.g. router.back()). */
  onExit?: () => void;
}

export function BookVaccineScreen({ onExit }: BookVaccineScreenProps) {
  const { config } = useTheme();
  const { user } = useAuth();
  const { vaccines, isLoading: vaccinesLoading } = useVaccines();
  const { template, isLoading: templateLoading } = useConsentTemplate();
  const submit = useSubmitVaccineConsent();

  // ── Wizard state ────────────────────────────────────────────────────────────
  const [stepIndex, setStepIndex] = useState(0);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [date, setDate] = useState('');
  const [time, setTime] = useState('');
  const [formData, setFormData] = useState<Record<string, any>>({});
  const [photos, setPhotos] = useState<UploadedPhoto[]>([]);
  const [insurancePhotos, setInsurancePhotos] = useState<UploadedPhoto[]>([]);
  const [signature, setSignature] = useState('');
  const [certified, setCertified] = useState(false);
  const [showErrors, setShowErrors] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [done, setDone] = useState<{ names: string[]; date?: string; time?: string } | null>(null);

  // Prefill name/phone/email from the signed-in user, once.
  React.useEffect(() => {
    if (!user || !template) return;
    setFormData((prev) => {
      if (Object.keys(prev).length) return prev;
      const seeded: Record<string, any> = {};
      const fullName = [user.firstname, user.lastname].filter(Boolean).join(' ');
      for (const f of template.fields ?? []) {
        if (f.type === 'text' && /\bname\b/i.test(f.label) && fullName) seeded[f.key] = fullName;
        if (f.type === 'phone' && user.phoneNumber) seeded[f.key] = user.phoneNumber;
        if (f.type === 'email' && user.email) seeded[f.key] = user.email;
      }
      return seeded;
    });
  }, [user, template]);

  // ── Derived ──────────────────────────────────────────────────────────────────
  const grouped = useMemo(() => groupFields(template?.fields ?? []), [template]);
  const detailSections = useMemo(() => groupDetailSections(grouped.details), [grouped.details]);
  const patientName = useMemo(
    () => deriveContact(template?.fields ?? [], formData).patientName,
    [template, formData],
  );
  // Insurance is its own admin-managed field list, shown as a dedicated step only
  // when the shared consent form requires it.
  const signatureField = useMemo(() => findSignatureField(template?.fields ?? []), [template]);
  const photoField = useMemo(() => findPhotoField(template?.fields ?? []), [template]);
  const showSignature = !!template?.signatureShown;
  const showPhoto = !!template?.photoShown;
  const signatureRequired = showSignature && !!signatureField?.required;
  const photoRequired = showPhoto && !!photoField?.required;

  const insuranceOn = !!template?.insuranceShown;
  const insuranceFields = useMemo(
    () => (insuranceOn ? template?.insuranceFields ?? [] : []),
    [insuranceOn, template],
  );
  const insuranceChoice = (formData[INSURANCE_CHOICE_KEY] ?? '') as InsuranceChoice | '';
  const visitDuration = template?.visitDuration ?? 15;
  const enabledDays = useMemo(
    () => new Set((template?.days ?? []).filter((d) => d.enabled).map((d) => d.day)),
    [template],
  );
  const hasSchedule = enabledDays.size > 0;
  const excluded = useMemo(() => new Set(template?.excludedDates ?? []), [template]);
  const primaryId = selectedIds[0];

  const { slots, isLoading: slotsLoading, refetch: refetchSlots } =
    useVaccineSlots(primaryId, date, visitDuration);

  const selectedVaccines = useMemo(
    () => vaccines.filter((v) => selectedIds.includes(v.id)),
    [vaccines, selectedIds],
  );

  const availableDates = useMemo(() => {
    if (!hasSchedule) return [];
    const out: Date[] = [];
    const today = startOfToday();
    for (let i = 0; i < DAYS_AHEAD; i++) {
      const d = addDays(today, i);
      const iso = format(d, 'yyyy-MM-dd');
      if (enabledDays.has(WEEKDAY_BY_INDEX[getDay(d)]) && !excluded.has(iso)) out.push(d);
    }
    return out;
  }, [hasSchedule, enabledDays, excluded]);

  // ── Dynamic steps ──────────────────────────────────────────────────────────────
  const steps: (WizardStep & { key: StepKey })[] = useMemo(() => {
    const s: (WizardStep & { key: StepKey })[] = [
      { key: 'select', label: 'Vaccines', icon: <Syringe className="h-5 w-5" /> },
    ];
    if (hasSchedule) s.push({ key: 'schedule', label: 'Time', icon: <Calendar className="h-5 w-5" /> });
    if (grouped.details.length) s.push({ key: 'details', label: 'Details', icon: <User className="h-5 w-5" /> });
    if (grouped.screening.length) s.push({ key: 'screening', label: 'Health', icon: <ClipboardList className="h-5 w-5" /> });
    if (insuranceOn) s.push({ key: 'insurance', label: 'Insurance', icon: <CreditCard className="h-5 w-5" /> });
    s.push({ key: 'sign', label: 'Sign', icon: <PenLine className="h-5 w-5" /> });
    s.push({ key: 'confirm', label: 'Confirm', icon: <CheckCircle2 className="h-5 w-5" /> });
    return s;
  }, [hasSchedule, grouped.details.length, grouped.screening.length, insuranceOn]);

  const current = steps[Math.min(stepIndex, steps.length - 1)]?.key;

  // ── Step validity ────────────────────────────────────────────────────────────
  const stepValid = (key: StepKey): boolean => {
    switch (key) {
      case 'select': return selectedIds.length > 0;
      case 'schedule': return !!date && !!time;
      case 'details': return !missingRequired(grouped.details, formData);
      case 'screening': return !missingRequired(grouped.screening, formData);
      case 'insurance':
        if (!insuranceChoice) return false;
        if (insuranceChoice === 'details') return !missingRequired(insuranceFields, formData);
        if (insuranceChoice === 'photo') return insurancePhotos.length > 0;
        return true;
      case 'sign':
        return certified
          && (!signatureRequired || !!signature)
          && (!photoRequired || photos.length > 0);
      case 'confirm': return true;
      default: return true;
    }
  };

  // Human-friendly message shown when the user hits Continue on an invalid step.
  const stepErrorMessage = (key: StepKey): string => {
    switch (key) {
      case 'select': return 'Please select at least one vaccine to continue.';
      case 'schedule': return !date ? 'Please pick a date for your visit.' : 'Please choose an available time.';
      case 'insurance':
        if (!insuranceChoice) return 'Please choose one of the insurance options to continue.';
        if (insuranceChoice === 'photo') return 'Please add a photo of your insurance card.';
        return 'Please fill in the required insurance fields highlighted below.';
      case 'sign':
        if (photoRequired && photos.length === 0) return 'Please upload the required photo.';
        if (signatureRequired && !signature) return 'Please add your signature.';
        return 'Please confirm the certification below to continue.';
      default: return 'Please fill in the required fields highlighted below.';
    }
  };

  const setField = (key: string, value: any) => setFormData((p) => ({ ...p, [key]: value }));

  const goNext = () => {
    if (!stepValid(current)) {
      setShowErrors(true);
      // Bring the error banner / first invalid field into view.
      if (typeof window !== 'undefined') window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    setShowErrors(false);
    setStepIndex((i) => Math.min(i + 1, steps.length - 1));
  };
  const goBack = () => {
    setShowErrors(false);
    if (stepIndex === 0) { onExit?.(); return; }
    setStepIndex((i) => i - 1);
  };

  const handleSubmit = async () => {
    setSubmitError('');
    const contact = deriveContact(template?.fields ?? [], formData);
    const userFullName = [user?.firstname, user?.lastname].filter(Boolean).join(' ');
    try {
      // Auto-fill the "vaccine(s) to be given today" field from the step-1 selection.
      const vaccinesStr = selectedVaccines.map((v) => v.name).join(', ');
      const autoFilled: Record<string, any> = {};
      for (const f of template?.fields ?? []) {
        if (isVaccinesTodayField(f)) autoFilled[f.key] = vaccinesStr;
      }

      // Start from the answers, then drop anything belonging to an insurance route
      // the patient didn't end on, so the pharmacist never sees stale details from
      // a route that was filled in and then switched away from.
      const answers: Record<string, any> = { ...formData };
      if (insuranceOn && insuranceChoice !== 'details') {
        for (const f of insuranceFields) delete answers[f.key];
      }
      if (insuranceOn && insuranceChoice === 'photo' && insurancePhotos.length) {
        answers[INSURANCE_PHOTOS_KEY] = insurancePhotos.map((p) => p.dataUrl);
      }

      await submit.mutateAsync({
        vaccineIds: selectedIds,
        patientName: contact.patientName || userFullName || 'Guest',
        patientEmail: contact.patientEmail || user?.email,
        patientPhone: contact.patientPhone,
        formData: {
          ...answers,
          ...autoFilled,
          ...(signature ? { signature } : {}),
          ...(photos.length ? { photos: photos.map((p) => p.dataUrl) } : {}),
          certified: true,
        },
        ...(hasSchedule && date && time ? { appointmentDate: date, appointmentTime: time } : {}),
      });
      setDone({ names: selectedVaccines.map((v) => v.name), date, time });
    } catch (err: any) {
      const status = err?.response?.status;
      if (status === 409) {
        // Slot was taken between selection and submit — send them back to reschedule.
        setSubmitError('That time was just booked. Please choose another slot.');
        setTime('');
        refetchSlots();
        const scheduleIdx = steps.findIndex((s) => s.key === 'schedule');
        if (scheduleIdx >= 0) setStepIndex(scheduleIdx);
      } else {
        setSubmitError('Something went wrong submitting your form. Please try again.');
      }
    }
  };

  // ── Loading / success ──────────────────────────────────────────────────────────
  if (vaccinesLoading || templateLoading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-hprimary-500" />
      </div>
    );
  }

  if (!vaccinesLoading && vaccines.length === 0) {
    return (
      <div className="mx-auto max-w-md px-4 py-20 text-center">
        <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-gray-100">
          <Syringe className="h-7 w-7 text-gray-400" />
        </div>
        <h2 className="text-lg font-semibold text-gray-900">No vaccines available</h2>
        <p className="mt-1 text-sm text-gray-500">{config.storeName} isn’t offering vaccinations online right now.</p>
      </div>
    );
  }

  if (done) return <SuccessView done={done} storeName={config.storeName} onExit={onExit} />;

  // ── Layout ──────────────────────────────────────────────────────────────────
  return (
    <div className="mx-auto max-w-2xl px-4 pb-32 pt-6 sm:pt-8">
      <header className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Book a vaccination</h1>
        <p className="mt-0.5 text-sm text-gray-500">at {config.storeName}</p>
      </header>

      <div className="mb-8">
        <VaccineStepper steps={steps} currentIndex={stepIndex} onStepClick={(i) => i < stepIndex && setStepIndex(i)} />
      </div>

      {showErrors && !stepValid(current) && (
        <motion.div
          initial={{ opacity: 0, y: -6 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-5 flex items-center gap-2.5 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-600"
        >
          <AlertTriangle className="h-4 w-4 shrink-0" />
          {stepErrorMessage(current)}
        </motion.div>
      )}

      <AnimatePresence mode="wait">
        <motion.div
          key={current}
          initial={{ opacity: 0, x: 16 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -16 }}
          transition={{ duration: 0.2 }}
        >
          {current === 'select' && (
            <StepShell title="Which vaccines?" subtitle="Select all you’d like in one visit.">
              <InsuranceDisclaimer className="mb-4" />
              <div className="grid gap-2.5">
                {vaccines.map((v) => {
                  const checked = selectedIds.includes(v.id);
                  return (
                    <button
                      key={v.id}
                      type="button"
                      onClick={() => setSelectedIds((ids) => checked ? ids.filter((i) => i !== v.id) : [...ids, v.id])}
                      className={`flex items-center justify-between gap-3 rounded-2xl border p-4 text-left transition-all
                        ${checked ? 'border-hprimary-500 bg-hprimary-50 shadow-sm' : 'border-gray-200 bg-white hover:border-hprimary-300'}`}
                    >
                      <div className="flex items-center gap-3">
                        <span className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-lg border transition-colors ${checked ? 'border-hprimary-500 bg-hprimary-500 text-white' : 'border-gray-300 bg-white'}`}>
                          {checked && <Check className="h-3.5 w-3.5" />}
                        </span>
                        <div>
                          <p className="font-medium text-gray-900">{v.name}</p>
                          {v.description && <p className="text-xs text-gray-500">{v.description}</p>}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </StepShell>
          )}

          {current === 'schedule' && (
            <StepShell title="Pick a time" subtitle={`Choose a slot for your ${visitDuration}-minute visit.`}>
              <DateStrip dates={availableDates} value={date} onChange={(d) => { setDate(d); setTime(''); }} />
              {date && (
                <div className="mt-5">
                  <p className="mb-2 text-sm font-medium text-gray-700">Available times</p>
                  {slotsLoading ? (
                    <div className="flex items-center gap-2 py-6 text-sm text-gray-400">
                      <Loader2 className="h-4 w-4 animate-spin" /> Loading times…
                    </div>
                  ) : slots.filter((s) => !s.isBooked).length === 0 ? (
                    <p className="py-6 text-sm text-gray-500">No open times this day. Try another date.</p>
                  ) : (
                    <div className="grid grid-cols-3 gap-2 sm:grid-cols-4">
                      {slots.map((s) => (
                        <button
                          key={s.time}
                          type="button"
                          disabled={s.isBooked}
                          onClick={() => setTime(s.time)}
                          className={`rounded-xl border py-2.5 text-sm font-medium transition-all
                            ${s.isBooked ? 'cursor-not-allowed border-gray-100 bg-gray-50 text-gray-300 line-through'
                              : time === s.time ? 'border-hsecondary-500 bg-hsecondary-500 text-white shadow-sm'
                              : 'border-gray-200 bg-white text-gray-700 hover:border-hsecondary-400'}`}
                        >
                          {to12h(s.time)}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </StepShell>
          )}

          {current === 'details' && (
            <StepShell title="Your details" subtitle="So the pharmacist can prepare your visit.">
              <div className="space-y-7">
                {detailSections.map((section) => (
                  <div key={section.key} className="space-y-4">
                    <div className="flex items-center gap-2.5">
                      <span className="text-hsecondary-600">{SECTION_ICON[section.key]}</span>
                      <h3 className="text-sm font-semibold text-gray-900">{section.title}</h3>
                      <span className="h-px flex-1 bg-gray-100" />
                    </div>
                    <div className="grid grid-cols-1 gap-x-4 gap-y-4 sm:grid-cols-2">
                      {section.fields.map((f) => {
                        const err = showErrors && f.required && isEmpty(formData[f.key]);
                        return (
                          <div key={f.key} className={isFullWidthField(f) ? 'sm:col-span-2' : ''}>
                            <ConsentField
                              field={f}
                              value={formData[f.key]}
                              onChange={(v) => setField(f.key, v)}
                              invalid={err}
                            />
                            {err && <p className="mt-1.5 text-xs text-red-500">This field is required.</p>}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </StepShell>
          )}

          {current === 'screening' && (
            <StepShell title="Health screening" subtitle="A few quick questions to confirm it’s safe to vaccinate you today.">
              <div className="space-y-3">
                {grouped.screening.map((f, i) => {
                  const err = showErrors && f.required && isEmpty(formData[f.key]);
                  if (isYesNo(f)) {
                    const val = String(formData[f.key] ?? '').toLowerCase();
                    return (
                      <div
                        key={f.key}
                        className={`rounded-2xl border p-4 transition-colors
                          ${err ? 'border-red-300 bg-red-50/40' : 'border-gray-200 bg-white'}`}
                      >
                        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                          <p className="text-sm leading-relaxed text-gray-800">
                            <span className="mr-1.5 font-semibold text-gray-300">{i + 1}.</span>
                            {f.label}
                            {f.required && <span className="text-red-500"> *</span>}
                          </p>
                          <div className="flex shrink-0 rounded-full bg-gray-100 p-0.5 sm:ml-4">
                            {['Yes', 'No'].map((opt) => {
                              const selected = val === opt.toLowerCase();
                              return (
                                <button
                                  key={opt}
                                  type="button"
                                  onClick={() => setField(f.key, opt.toLowerCase())}
                                  className={`min-w-[3.5rem] rounded-full px-4 py-1.5 text-sm font-medium transition-all
                                    ${selected ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                                >
                                  {opt}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                        {err && <p className="mt-2.5 text-xs text-red-500">Please select an answer.</p>}
                      </div>
                    );
                  }
                  return (
                    <div key={f.key} className={`rounded-2xl border p-4 ${err ? 'border-red-300' : 'border-gray-200 bg-white'}`}>
                      <ConsentField
                        field={f}
                        value={formData[f.key]}
                        onChange={(v) => setField(f.key, v)}
                        invalid={err}
                      />
                      {err && <p className="mt-2.5 text-xs text-red-500">Please make a selection.</p>}
                    </div>
                  );
                })}
              </div>
            </StepShell>
          )}

          {current === 'insurance' && (
            <StepShell title="Insurance" subtitle="However works best for you — none of these hold up your booking.">
              <div className="grid gap-2.5">
                {INSURANCE_CHOICES.map((opt) => {
                  const Icon = INSURANCE_ICON[opt.value];
                  const active = insuranceChoice === opt.value;
                  // Only the chosen route needs a field list, so the others stay collapsed.
                  const expandable = opt.value === 'details' ? insuranceFields.length > 0 : opt.value === 'photo';
                  return (
                    <div
                      key={opt.value}
                      className={`overflow-hidden rounded-2xl border transition-all
                        ${active ? 'border-hprimary-500 bg-hprimary-50/50 shadow-sm' : 'border-gray-200 bg-white hover:border-hprimary-300'}`}
                    >
                      <button
                        type="button"
                        onClick={() => setField(INSURANCE_CHOICE_KEY, opt.value)}
                        className="flex w-full items-center gap-3 p-4 text-left"
                      >
                        <span className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full border-2 transition-colors
                          ${active ? 'border-hprimary-500 bg-hprimary-500 text-white' : 'border-gray-300 bg-white'}`}>
                          {active && <Check className="h-3.5 w-3.5" />}
                        </span>
                        <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-xl transition-colors
                          ${active ? 'bg-hprimary-100 text-hprimary-600' : 'bg-gray-100 text-gray-400'}`}>
                          <Icon className="h-4.5 w-4.5" />
                        </span>
                        <span className="min-w-0">
                          <span className="block text-sm font-medium text-gray-900">{opt.label}</span>
                          <span className="block text-xs text-gray-500">{opt.hint}</span>
                        </span>
                      </button>

                      {active && expandable && (
                        <div className="border-t border-hprimary-100 bg-white/70 px-4 py-4">
                          {opt.value === 'details' ? (
                            <div className="grid grid-cols-1 gap-x-4 gap-y-4 sm:grid-cols-2">
                              {insuranceFields.map((f) => {
                                const err = showErrors && f.required && isEmpty(formData[f.key]);
                                return (
                                  <div key={f.key} className={isFullWidthField(f) ? 'sm:col-span-2' : ''}>
                                    <ConsentField
                                      field={f}
                                      value={formData[f.key]}
                                      onChange={(v) => setField(f.key, v)}
                                      invalid={err}
                                    />
                                    {err && <p className="mt-1.5 text-xs text-red-500">This field is required.</p>}
                                  </div>
                                );
                              })}
                            </div>
                          ) : (
                            <PhotoUpload
                              photos={insurancePhotos}
                              onChange={setInsurancePhotos}
                              hint="Front and back of your card."
                            />
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </StepShell>
          )}

          {current === 'sign' && (
            <StepShell title="Review & sign" subtitle="Confirm the information you provided is accurate.">
              <div className="space-y-5">
                {showPhoto && (
                  <div>
                    <p className="mb-1.5 text-sm font-medium text-gray-700">
                      {photoField?.label || 'Upload a photo'}
                      {photoRequired && <span className="ml-0.5 text-red-500">*</span>}
                    </p>
                    <PhotoUpload photos={photos} onChange={setPhotos} hint="e.g. insurance card or ID." />
                  </div>
                )}
                {showSignature && (
                  <div>
                    <p className="mb-1.5 text-sm font-medium text-gray-700">
                      Signature
                      {signatureRequired && <span className="ml-0.5 text-red-500">*</span>}
                    </p>
                    <SignaturePad value={signature} onChange={setSignature} />
                  </div>
                )}
                <label className={`flex cursor-pointer items-start gap-3 rounded-xl border p-4 transition-colors ${showErrors && !certified ? 'border-red-300 bg-red-50/40' : 'border-gray-200'}`}>
                  <span className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-md border transition-colors ${certified ? 'border-hprimary-500 bg-hprimary-500 text-white' : 'border-gray-300 bg-white'}`}>
                    {certified && <Check className="h-3.5 w-3.5" />}
                  </span>
                  <input type="checkbox" className="sr-only" checked={certified} onChange={(e) => setCertified(e.target.checked)} />
                  <span className="text-sm text-gray-700">
                    I certify that the information I’ve provided is true and complete to the best of my knowledge.
                  </span>
                </label>
              </div>
            </StepShell>
          )}

          {current === 'confirm' && (
            <StepShell title="Confirm your booking" subtitle="A quick review before you submit.">
              <div className="overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-sm">
                {/* Appointment header */}
                {hasSchedule && date && time ? (
                  <div className="flex items-center gap-3.5 border-b border-gray-100 bg-hsecondary-50 px-5 py-4">
                    <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-hsecondary-500 text-white shadow-sm">
                      <Calendar className="h-5 w-5" />
                    </span>
                    <div className="min-w-0">
                      <p className="font-semibold text-gray-900">{format(new Date(`${date}T00:00:00`), 'EEEE, MMMM d')}</p>
                      <p className="text-sm text-gray-500">{to12h(time)} · {config.storeName}</p>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center gap-3.5 border-b border-gray-100 bg-hsecondary-50 px-5 py-4">
                    <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-hsecondary-500 text-white shadow-sm">
                      <Syringe className="h-5 w-5" />
                    </span>
                    <div className="min-w-0">
                      <p className="font-semibold text-gray-900">Walk-in visit</p>
                      <p className="text-sm text-gray-500">Visit {config.storeName} any time we&rsquo;re open</p>
                    </div>
                  </div>
                )}

                {/* Itemized vaccines */}
                <div className="px-5 py-4">
                  <p className="mb-3 text-xs font-medium uppercase tracking-wide text-gray-400">
                    Vaccine{selectedVaccines.length > 1 ? 's' : ''}
                  </p>
                  <div className="space-y-3">
                    {selectedVaccines.map((v) => (
                      <div key={v.id} className="flex items-center justify-between gap-3">
                        <div className="flex items-center gap-2.5 min-w-0">
                          <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-lg bg-hsecondary-50 text-hsecondary-600">
                            <Check className="h-3.5 w-3.5" />
                          </span>
                          <span className="truncate text-sm text-gray-800">{v.name}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <InsuranceDisclaimer className="mt-4" />

              {/* Reassurance */}
              <div className="mt-4 space-y-2.5">
                {patientName && (
                  <div className="flex items-center gap-2.5 text-sm text-gray-500">
                    <User className="h-4 w-4 shrink-0 text-gray-400" />
                    Booking for <span className="font-medium text-gray-700">{patientName}</span>
                  </div>
                )}
              </div>

              {submitError && (
                <p className="mt-4 rounded-xl bg-red-50 px-3.5 py-2.5 text-sm text-red-600">{submitError}</p>
              )}
            </StepShell>
          )}
        </motion.div>
      </AnimatePresence>

      {/* Sticky footer */}
      <div className="fixed inset-x-0 bottom-0 z-20 border-t border-gray-100 bg-white/95 backdrop-blur-sm">
        <div className="mx-auto flex max-w-2xl items-center justify-between gap-3 px-4 py-3">
          <button
            type="button"
            onClick={goBack}
            className="inline-flex items-center gap-1.5 rounded-full px-4 py-2.5 text-sm font-medium text-gray-600 transition-colors hover:bg-gray-100"
          >
            <ArrowLeft className="h-4 w-4" /> Back
          </button>

          {current === 'select' && selectedIds.length > 0 && (
            <span className="text-sm text-gray-500">{selectedIds.length} selected</span>
          )}

          {current === 'confirm' ? (
            <button
              type="button"
              onClick={handleSubmit}
              disabled={submit.isPending}
              className="inline-flex items-center gap-2 rounded-full bg-hprimary-600 px-6 py-2.5 text-sm font-semibold text-white shadow-md transition-all hover:bg-hprimary-700 disabled:opacity-60"
            >
              {submit.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
              {submit.isPending ? 'Submitting…' : 'Confirm booking'}
            </button>
          ) : (
            <button
              type="button"
              onClick={goNext}
              className="inline-flex items-center gap-2 rounded-full bg-hprimary-600 px-6 py-2.5 text-sm font-semibold text-white shadow-md transition-all hover:bg-hprimary-700"
            >
              Continue <ArrowRight className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Step keys ────────────────────────────────────────────────────────────────────
type StepKey = 'select' | 'schedule' | 'details' | 'screening' | 'insurance' | 'sign' | 'confirm';

// ── Small presentational helpers ────────────────────────────────────────────────
export function StepShell({ title, subtitle, children }: { title: string; subtitle?: string; children: React.ReactNode }) {
  return (
    <div>
      <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
      {subtitle && <p className="mb-5 mt-0.5 text-sm text-gray-500">{subtitle}</p>}
      {children}
    </div>
  );
}

export function DateStrip({ dates, value, onChange }: { dates: Date[]; value: string; onChange: (iso: string) => void }) {
  if (dates.length === 0) {
    return <p className="py-6 text-sm text-gray-500">No upcoming availability. Please check back later.</p>;
  }
  return (
    <div className="flex gap-2 overflow-x-auto pb-2 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
      {dates.map((d) => {
        const iso = format(d, 'yyyy-MM-dd');
        const active = value === iso;
        return (
          <button
            key={iso}
            type="button"
            onClick={() => onChange(iso)}
            className={`flex w-14 shrink-0 flex-col items-center rounded-xl border py-2.5 transition-all
              ${active ? 'border-hsecondary-500 bg-hsecondary-500 text-white shadow-sm' : 'border-gray-200 bg-white text-gray-600 hover:border-hsecondary-400'}`}
          >
            <span className="text-[10px] font-medium uppercase opacity-80">{format(d, 'EEE')}</span>
            <span className="text-lg font-bold leading-tight">{format(d, 'd')}</span>
            <span className="text-[10px] opacity-80">{format(d, 'MMM')}</span>
          </button>
        );
      })}
    </div>
  );
}

export function SuccessView({ done, storeName, onExit }: { done: { names: string[]; date?: string; time?: string }; storeName: string; onExit?: () => void }) {
  const hasAppt = !!(done.date && done.time);
  return (
    <div className="mx-auto max-w-md px-4 py-14 sm:py-20">
      {/* Animated check with radiating rings */}
      <div className="relative mx-auto mb-7 flex h-24 w-24 items-center justify-center">
        {[0, 1].map((i) => (
          <motion.span
            key={i}
            className="absolute inset-0 rounded-full bg-hsecondary-500/20"
            initial={{ scale: 0.6, opacity: 0.6 }}
            animate={{ scale: 1.6, opacity: 0 }}
            transition={{ duration: 1.8, repeat: Infinity, delay: i * 0.6, ease: 'easeOut' }}
          />
        ))}
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ type: 'spring', stiffness: 380, damping: 22 }}
          className="relative flex h-20 w-20 items-center justify-center rounded-full bg-hsecondary-500 shadow-lg shadow-hsecondary-500/30"
        >
          <motion.span initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: 0.15, type: 'spring', stiffness: 500, damping: 20 }}>
            <Check className="h-10 w-10 text-white" strokeWidth={2.5} />
          </motion.span>
        </motion.div>
      </div>

      <h2 className="text-center text-2xl font-bold text-gray-900">You&rsquo;re all set!</h2>
      <p className="mx-auto mt-2 max-w-xs text-center text-sm leading-relaxed text-gray-500">
        {hasAppt ? 'Your appointment is confirmed.' : 'Your request has been received.'}
      </p>

      {/* Booking summary card */}
      <div className="mt-7 space-y-4 rounded-2xl border border-gray-100 bg-white p-5 shadow-sm">
        {hasAppt && (
          <div className="flex items-center gap-3">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-hsecondary-50 text-hsecondary-600">
              <Clock className="h-5 w-5" />
            </span>
            <div>
              <p className="font-semibold text-gray-900">{format(new Date(`${done.date}T00:00:00`), 'EEEE, MMMM d')}</p>
              <p className="text-sm text-gray-500">{to12h(done.time!)} · {storeName}</p>
            </div>
          </div>
        )}
        <div className={`flex items-start gap-3 ${hasAppt ? 'border-t border-gray-100 pt-4' : ''}`}>
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-hsecondary-50 text-hsecondary-600">
            <Syringe className="h-5 w-5" />
          </span>
          <div>
            <p className="text-xs font-medium uppercase tracking-wide text-gray-400">Vaccines</p>
            <div className="mt-1.5 flex flex-wrap gap-1.5">
              {done.names.map((n) => (
                <span key={n} className="rounded-full bg-gray-100 px-2.5 py-0.5 text-sm font-medium text-gray-700">{n}</span>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="mt-4 flex items-start gap-2.5 rounded-xl bg-gray-50 px-4 py-3">
        <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-hsecondary-500" />
        <p className="text-xs leading-relaxed text-gray-500">
          Bring your insurance card and a photo ID. {hasAppt ? 'Please arrive a few minutes early.' : "Walk in any time we're open."}
        </p>
      </div>

      {onExit && (
        <button
          type="button"
          onClick={onExit}
          className="mt-6 w-full rounded-full bg-hprimary-600 px-6 py-3 text-sm font-semibold text-white shadow-md transition-colors hover:bg-hprimary-700"
        >
          Done
        </button>
      )}
    </div>
  );
}

// ── utils ────────────────────────────────────────────────────────────────────────
export function isEmpty(v: any) {
  if (Array.isArray(v)) return v.length === 0;
  return v == null || String(v).trim() === '';
}

export function to12h(hhmm: string) {
  const [h, m] = hhmm.split(':').map(Number);
  const period = h >= 12 ? 'PM' : 'AM';
  const hour = h % 12 === 0 ? 12 : h % 12;
  return `${hour}:${String(m).padStart(2, '0')} ${period}`;
}
